import { Navigate, Outlet, useLocation } from 'react-router-dom';

import type { PermissionCode } from '@solar/contracts';

import { PageSpinner } from '../../components/PageSpinner';
import { paths } from '../../routes/paths';

import { useAuth } from './auth-context';

/**
 * Gates a branch of the route tree behind a session and, optionally, a
 * permission.
 *
 * This is a usability boundary, not a security one. It stops an unauthorised
 * user seeing a screen that would only fill with errors; the API refuses the
 * underlying requests regardless of what the router allows.
 */
export function RequireAuth({ permission }: { permission?: PermissionCode }): React.JSX.Element {
  const { status, can } = useAuth();
  const location = useLocation();

  if (status === 'bootstrapping') {
    // Redirecting here would sign out every user who reloads the page, because
    // the refresh exchange has not finished yet.
    return <PageSpinner label="Restoring your session" />;
  }

  if (status === 'anonymous') {
    // `state.from` lets the sign-in page return the user where they were
    // heading instead of dropping them on a generic landing page.
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={paths.login} replace state={{ from: returnTo }} />;
  }

  if (permission !== undefined && !can(permission)) {
    return <Navigate to={paths.unauthorized} replace />;
  }

  return <Outlet />;
}

/** Keeps a signed-in user away from the sign-in and registration screens. */
export function RequireAnonymous(): React.JSX.Element {
  const { status } = useAuth();

  if (status === 'bootstrapping') {
    return <PageSpinner label="Loading" />;
  }

  if (status === 'authenticated') {
    return <Navigate to={paths.dashboard} replace />;
  }

  return <Outlet />;
}
